import React, { useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl, TextField, IconButton, Button, Stack, Box } from '@mui/material';
import { DeleteOutlined } from '@mui/icons-material';
import { DUPLICATE_EMAIL_MESSAGE, INVALID_EMAIL_MESSAGE, isRepeatedEmail, isValidEmail } from './email-utils';
import removeIndex from '../array/remove-index';

const EmailRecipients = ({ name }) => {
    const { control } = useFormContext();
    const [errors, setErrors] = useState({});
    const inputRefs = useRef([]);

    const validateEmail = (email) => !!email?.trim() && isValidEmail(email);

    const updateEmail = (emails, index, value, onChange) => {
        const newEmails = [...emails];
        newEmails[index] = value;
        onChange(newEmails);
    };

    const clearError = (index) => {
        setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[index];
            return newErrors;
        });
    };

    return (
        <FormControl fullWidth>
            <Controller
                name={name}
                control={control}
                defaultValue={[]}
                render={({ field: { onChange, value, ref }, fieldState }) => {
                    const emails = value || [];
                    const focusFirstInvalidRow = () => {
                        const firstInvalidIndex = emails.findIndex((email, index) => !validateEmail(email) || isRepeatedEmail(emails, index));
                        inputRefs.current[Math.max(firstInvalidIndex, 0)]?.focus();
                    };
                    ref({ focus: focusFirstInvalidRow, select: () => {} });
                    const rowError = (index) => errors[index] ?? fieldState.error?.[index]?.message;
                    const hasInvalidEmail = emails.some((email, index) => {
                        if (!validateEmail(email)) return true;
                        if (isRepeatedEmail(emails, index)) return true;
                        return !!errors[index];
                    });

                    const handleEmailChange = (index, newValue) => {
                        updateEmail(emails, index, newValue, onChange);
                        clearError(index);
                    };

                    const handleEmailBlur = (index) => {
                        const email = emails[index];
                        if (!email || email.trim() === '') {
                            setErrors((prev) => ({ ...prev, [index]: 'Email is required' }));
                        } else if (!validateEmail(email)) {
                            setErrors((prev) => ({ ...prev, [index]: INVALID_EMAIL_MESSAGE }));
                        } else if (isRepeatedEmail(emails, index)) {
                            setErrors((prev) => ({ ...prev, [index]: DUPLICATE_EMAIL_MESSAGE }));
                        } else {
                            updateEmail(emails, index, email.trim(), onChange);
                            clearError(index);
                        }
                    };

                    const handleDeleteEmail = (index) => {
                        const newEmails = emails.filter((_, i) => i !== index);
                        onChange(newEmails);
                        setErrors((prev) => removeIndex(prev, index));
                    };

                    const handleAddEmail = () => {
                        onChange([...emails, '']);
                    };

                    return (
                        <Stack spacing={2}>
                            {emails.map((email, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Box key={`${name}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextField
                                        label={`Recipient Email ${index + 1}`} // wrap
                                        value={email}
                                        inputRef={(element) => {
                                            inputRefs.current[index] = element;
                                        }}
                                        onChange={(e) => handleEmailChange(index, e.target.value)}
                                        onBlur={() => handleEmailBlur(index)}
                                        error={!!rowError(index)}
                                        helperText={rowError(index)}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />
                                    <IconButton
                                        color="error" // wrap
                                        onClick={() => handleDeleteEmail(index)}
                                    >
                                        <DeleteOutlined />
                                    </IconButton>
                                </Box>
                            ))}
                            <Button variant="outlined" onClick={handleAddEmail} disabled={hasInvalidEmail} sx={{ fontSize: '14px', fontWeight: 600, textTransform: 'none' }}>
                                Add Email
                            </Button>
                        </Stack>
                    );
                }}
            />
        </FormControl>
    );
};

export default EmailRecipients;
